import React from "react";
import { FootballResult } from "./FootballGameRun.tsx";
import { prettyFront } from "./footballGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface FootballGameSummaryProps {
    result: FootballResult;
    /** Best score for these facts and this defense, after this game. */
    best: number | null;
    isNewBest: boolean;
    onPlayAgain(): void;
    /** Back to the picker, where the defense and the facts are both chosen. */
    onChangeDefense(): void;
}

/**
 * The final whistle: points, then the drive's counts, then every fact that cost
 * a down with its answer - a wrong throw says who the ball went to, a sack says
 * only that the rush got there first (ADR 0103 §1).
 */
export function FootballGameSummary({ result, best, isNewBest, onPlayAgain, onChangeDefense }: FootballGameSummaryProps) {
    const t = useTranslation();

    return (
        <div className="quizSummary footballSummary">
            <p className="quizSummaryCounts">{t("flashCards.footballFinal")}</p>
            <p className="quizSummaryScore">{result.points}</p>
            <p className="quizSummaryScoreLabel">{t("flashCards.footballPointsCaption")}</p>
            <p className="quizSummaryCounts">
                {format(t("flashCards.footballCountsTemplate"), {
                    touchdowns: result.touchdowns,
                    completions: result.completions,
                    attempts: result.attempts,
                    sacks: result.sacks,
                })}
            </p>

            {best !== null && (
                <p className={`quizSummaryBest${isNewBest ? " quizSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            {result.misses.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                    <ul>
                        {result.misses.map((miss, i) => (
                            <li key={i}>
                                <span className="quizSummaryMissProblem">
                                    {prettyFront(miss.front)} = {miss.answer}
                                </span>
                                <span className="quizSummaryMissYours">
                                    {miss.picked === null
                                        ? t("flashCards.footballSackedLower")
                                        : format(t("flashCards.footballYouThrewTemplate"), { n: miss.picked })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button type="button" className="quizPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>
            <button type="button" className="quizChangeMode" onClick={onChangeDefense}>
                {t("flashCards.footballChangeDefense")}
            </button>
        </div>
    );
}
